import type { Member } from '@ebalance/proto/bffuser/v1/user_directory_model_pb'
import type { TFunction } from 'i18next'

/**
 * 閲覧者が承認・差戻しをした時に記録される区分 (spec AC-WFW-007a / 007c)。値は backend が
 * 返し、画面は判定しない。どちらも操作を止める理由にはならず、常時の注意表示と確認モーダルを
 * 出す条件になる。承認記録・差戻し記録に併記された区分の表示にも同じ形を使う。
 */
export type ApprovalViewerRelation = {
  /** 「承認者非該当」: 閲覧者が対象ステージの承認者リストに含まれない。 */
  isApproverNotApplicable: boolean
  /** 「自己承認」: 閲覧者が当該サブステップの申請者本人 (実行者完了の操作者) である。 */
  isSelfApproval: boolean
}

export const needsApprovalConfirmation = (
  relation: ApprovalViewerRelation,
): boolean => relation.isApproverNotApplicable || relation.isSelfApproval

/**
 * 承認者が 1 人も存在しないステージか (spec AC-WFW-007a (a): 承認段階が未設定、または設定済の
 * 承認者が全員削除・無効化されている)。`members` は有効なメンバーだけの一覧 (UserDirectory の
 * ListMembers)。一覧が空の間 (読み込み中・取得失敗) は無効かどうかを判断できないため、設定済みの
 * 人数だけで判断する。
 */
export const hasNoActiveApprovers = (
  approverMemberIds: readonly string[],
  members: readonly Member[],
): boolean => {
  if (approverMemberIds.length === 0) return true
  if (members.length === 0) return false
  return approverMemberIds.every(
    (id) => !members.some((member) => member.memberId === id),
  )
}

/**
 * 承認記録・差戻し記録に併記された「承認者非該当」「自己承認」を、操作者名の後ろに
 * 添えた表示文字列にする (spec AC-WFW-007a / 007c)。どちらも無ければ名前だけを返す。
 */
export const formatNameWithRecordKinds = (
  t: TFunction,
  name: string,
  record: Partial<ApprovalViewerRelation> & {
    /** 「担当者非該当」: 完了者が担当者に含まれない (spec AC-WFW-007e)。 */
    isAssigneeNotApplicable?: boolean
  },
): string => {
  const kinds: string[] = []
  if (record.isApproverNotApplicable) {
    kinds.push(t('page:workflowCaseDetail.approval.recordKind.notApprover'))
  }
  if (record.isSelfApproval) {
    kinds.push(t('page:workflowCaseDetail.approval.recordKind.selfApproval'))
  }
  if (record.isAssigneeNotApplicable) {
    kinds.push(t('page:workflowCaseDetail.approval.recordKind.notAssignee'))
  }
  return kinds.length === 0
    ? name
    : t('page:workflowCaseDetail.approval.recordedBy', {
        name,
        kinds: kinds.join(
          t('page:workflowCaseDetail.approval.recordKind.separator'),
        ),
      })
}
